import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { importEndpoints } from '@ooh/contracts';
import { useApiClient } from '../../plugs/api-client';
import { SupplierDedupPhase } from './supplier-dedup-phase';
import type { AmbiguousSupplierEntry } from './supplier-dedup-phase';

export interface SupplierDedupSectionProps {
  importJobId: number;
  ambiguousSuppliers: AmbiguousSupplierEntry[];
  onNext: () => void;
}

export function SupplierDedupSection({
  importJobId,
  ambiguousSuppliers,
  onNext,
}: SupplierDedupSectionProps) {
  const { t } = useTranslation('mediaImport');
  const api = useApiClient();

  const saveResolutionsMutation = useMutation({
    mutationFn: (resolutions: Record<string, string | null>) =>
      api.call(importEndpoints.saveSupplierResolutions, { id: importJobId, resolutions }),
  });

  return (
    <SupplierDedupPhase
      ambiguousSuppliers={ambiguousSuppliers}
      onNext={onNext}
      labels={{
        heading: t('supplierDedup.heading'),
        subheading: t('supplierDedup.subheading'),
        useExistingLabel: t('supplierDedup.useExistingLabel'),
        similaritySuffix: t('supplierDedup.similaritySuffix'),
        createNewLabel: t('supplierDedup.createNewLabel'),
        nextButton: t('supplierDedup.nextButton'),
        submitting: t('supplierDedup.submitting'),
        errorGeneric: t('supplierDedup.errorGeneric'),
        columnFileName: t('supplierDedup.columnFileName'),
        columnAction: t('supplierDedup.columnAction'),
        columnSuggestions: t('supplierDedup.columnSuggestions'),
        allDecidedHint: t('supplierDedup.allDecidedHint'),
      }}
      onSaveResolutions={async (resolutions) => {
        await saveResolutionsMutation.mutateAsync(resolutions);
      }}
      isSaving={saveResolutionsMutation.isPending}
      errorMessage={saveResolutionsMutation.isError ? t('supplierDedup.errorGeneric') : null}
    />
  );
}
